import { ContentfulBlocks } from '@dreamstack/accentro-contentful/src/contentful/ContentfulBlocks'
import { PageBlockSpacingContainer } from '@dreamstack/accentro-contentful/src/contentful/PageBlockSpacingContainer'
import type { GetStaticPropsPlusOptionsGetProps } from '@dreamstack/artemis'
import {
  OtherLocaleUrlsContextProvider,
  allLocales,
  useLocale,
  useOtherLocale,
  useTranslation,
} from '@dreamstack/i18n'
import {
  NewsSearchResultBox,
  SearchAutocomplete,
  useHasSearchContext,
  useNewsSearchHitsAsArticles,
  useSearchIsSearching,
} from '@dreamstack/search'
import { DreamstackSeo } from '@dreamstack/seo'
import { trpc } from '@dreamstack/trpc/client'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import filter from 'lodash/filter'
import flatMap from 'lodash/flatMap'
import head from 'lodash/head'
import map from 'lodash/map'
import tail from 'lodash/tail'
import trim from 'lodash/trim'
import type { FunctionComponent } from 'react'
import { useMemo, useState } from 'react'
import { useGetNewsTypeUrlPartReverse } from '../hooks/usegetNewsTypeUrlPartReverse'
import { MainLayout } from '../layout/MainLayout'
import { HeadingH4 } from '../layout/Texts'
import type { HeroProps } from '../layout/Hero'
import { IS_ACCENTRO_WEB } from '../config/config'
import { NewsHeader } from './NewsHeader'
import { NewsTypes } from './NewsTypes'
import { ArticlePreviewGrid } from './ArticlePreviewGrid'
import { FirstArticle } from './FirstArticle'
import { LoadMoreNews } from './LoadMoreNews'
import {
  newsHubOtherLocaleUrls as baseNewsHubOtherLocaleUrls,
  getNewsPath,
  getNewsTypeUrlPart,
  newsBaseUrls,
} from './getNewsPath'
import type { NewsType } from './newsTypeUrls.static'

const FIRST_ARTICLE_BIG = IS_ACCENTRO_WEB

const NewsTypesAndSearchWrapper = ({
  children,
}: React.PropsWithChildren<{}>) => (
  <div className="flex flex-col space-y-px-32 md:flex-row md:space-y-0 md:mr-px-16">
    {children}
  </div>
)

const NewsTypesWrapper = ({
  isClosed,
  children,
}: React.PropsWithChildren<{ isClosed: boolean }>) => (
  <div className={[isClosed ? 'hidden' : '', 'md:block'].join(' ')}>
    {children}
  </div>
)

const FilterArticlesButtonWrapper = ({
  children,
}: React.PropsWithChildren<{}>) => (
  <div className="md:hidden lg:hidden">{children}</div>
)

export type NewsHubProps = {
  showSubhero?: boolean
  newsTypes: NewsType[]
}

export const NewsHub: FunctionComponent<
  React.PropsWithChildren<NewsHubProps>
> = ({ showSubhero = true, newsTypes }) => {
  const [isClosed, setIsClosed] = useState(true)
  const locale = useLocale()
  const newsType = useGetNewsTypeUrlPartReverse()
  const { data: articles } = trpc.cms.getNewsArticlePreviews.useQuery(
    { newsType: newsType ?? undefined, locale },
    {
      trpc: { ssr: true },
      staleTime: Infinity,
    }
  )
  const newsArticles = articles?.items

  const { firstArticle, otherArticles, length } = useMemo(() => {
    const filteredNewsArticles = filter(
      newsArticles,
      (f) => !newsType || f?.newsType === newsType
    )
    const firstArticle = FIRST_ARTICLE_BIG ? head(filteredNewsArticles) : null
    const otherArticles = FIRST_ARTICLE_BIG
      ? tail(filteredNewsArticles)
      : filteredNewsArticles
    const length = filteredNewsArticles?.length || 0
    return { firstArticle, otherArticles, length }
  }, [newsArticles, newsType])

  const topSixNewsSearchArticles = filter(
    newsArticles,
    (f) => !newsType || f?.newsType === newsType
  ).slice(0, 6)

  const t = useTranslation()

  const subHero = showSubhero ? <NewsHeader /> : undefined
  // STATIC PAGE STUFF
  const slug = trim(newsBaseUrls[locale], '/')

  const { data } = trpc.cms.getStaticPage.useQuery(
    { locale, slug },
    {
      trpc: { ssr: true },
      staleTime: Infinity,
    }
  )

  const { data: nestedNewsPage } = trpc.cms.getStaticPageByNewstype.useQuery(
    { locale, newsType: newsType ?? '' },
    {
      trpc: { ssr: true },
      staleTime: Infinity,
      enabled: !!newsType && IS_ACCENTRO_WEB,
    }
  )
  const hero = data?.page?.hero

  const filterNewsButtonsHandleClick = () => {
    setIsClosed(!isClosed)
  }

  // SEARCH
  const isSearching = useSearchIsSearching()
  const hasSearch = useHasSearchContext()
  const newsSearchArticles = useNewsSearchHitsAsArticles()

  // OTHER LOCALE URLs WITH NEWSTYPE
  // copy base urls which may get overwritten
  const newsHubOtherLocaleUrls = { ...baseNewsHubOtherLocaleUrls }

  const otherLocale = useOtherLocale()

  // if a newstype is selected and other locale exists:
  if (!!newsType && otherLocale) {
    //get the newspath translated for the newstype
    const hrefOtherLocale = getNewsPath({
      locale: otherLocale,
      newsType: newsType as NewsType,
      slug: '',
    })

    //overwrite the url of the other locale
    newsHubOtherLocaleUrls[otherLocale] = {
      url: hrefOtherLocale,
    }
  }

  const pillarPageTitle = nestedNewsPage?.page?.seo?.title
  const title = !!pillarPageTitle
    ? pillarPageTitle
    : t('accentro:newsHub.title')

  return (
    <OtherLocaleUrlsContextProvider context={newsHubOtherLocaleUrls}>
      <DreamstackSeo title={title} />
      <MainLayout
        hero={
          hero && hero.enable
            ? ({ ...hero, noMarginBottom: true } as HeroProps)
            : {
                title,
                forceDarkBg: true,
                size: 'small',
                noMarginBottom: true,
              }
        }
        subHero={subHero}
      >
        <PageBlockSpacingContainer>
          <NewsTypesAndSearchWrapper>
            {isSearching ? (
              <NewsSearchResultBox />
            ) : (
              <>
                <FilterArticlesButtonWrapper>
                  <HeadingH4>
                    <a
                      onClick={filterNewsButtonsHandleClick}
                      className="cursor-pointer text-accentroAqua-full text-lg"
                      onKeyPress={filterNewsButtonsHandleClick}
                      role="button"
                      tabIndex={0}
                    >
                      {t('accentro:newsHub.filterLabel')}
                      {!isClosed ? (
                        <ExpandLessIcon fontSize="large" />
                      ) : (
                        <ExpandMoreIcon fontSize="large" />
                      )}
                    </a>
                  </HeadingH4>
                </FilterArticlesButtonWrapper>
                <NewsTypesWrapper isClosed={isClosed}>
                  <NewsTypes newsTypes={newsTypes} />
                </NewsTypesWrapper>
              </>
            )}
            {hasSearch && (
              <div className="md:min-w-[380px]">
                <SearchAutocomplete indexType="news" />
              </div>
            )}
          </NewsTypesAndSearchWrapper>
          {!length && (
            <HeadingH4 className="text-center my-px-64">
              {t('accentro:newsHub.noArticles')}
            </HeadingH4>
          )}
          {!isSearching && !nestedNewsPage && firstArticle && (
            <FirstArticle article={firstArticle} />
          )}
          {!isSearching && nestedNewsPage && (
            <PageBlockSpacingContainer>
              <ArticlePreviewGrid articles={topSixNewsSearchArticles} />
              <ContentfulBlocks
                customProps={{
                  newsType: nestedNewsPage?.page?.newsType,
                  region: nestedNewsPage?.page?.region,
                }}
                blocks={nestedNewsPage?.blocksData ?? []}
              />
            </PageBlockSpacingContainer>
          )}

          <LoadMoreNews
            articles={isSearching ? newsSearchArticles : otherArticles}
          />
        </PageBlockSpacingContainer>
      </MainLayout>
    </OtherLocaleUrlsContextProvider>
  )
}

export const getStaticNewsProps: GetStaticPropsPlusOptionsGetProps = async ({
  params,
  locale,
}) => {
  // TODO: remove this everywhere
  if (!locale) {
    return {
      notFound: true,
    }
  }
}

export const getStaticNewsTypePaths = (newsTypes: NewsType[]) => {
  return flatMap(allLocales, (locale) => {
    return map(newsTypes, (newsType) => {
      const newsTypeUrl = getNewsTypeUrlPart({ locale, newsType })
      return {
        params: {
          newsTypeUrl,
        },
        locale,
      }
    })
  })
}
